import { useEffect, useMemo, useState } from 'react';
import { DateValueType, downloadFile, formatDate } from '@lib/utils.ts';
import {
  Button,
  Card,
  DateRangePicker,
  Divider,
  RadixTable,
  TColumn,
} from '@features/ui';
import Pagination from '@features/ui/components/pagination.tsx';
import { TReportBlacklistUser, TReportBlacklistUsersParams } from '@src/types';
import {
  exportBlacklistUsers,
  getBlacklistUsers,
} from '@src/services/reports.ts';
import { ExportIcon } from '@features/ui/icons';

export const BlacklistedUsers = () => {
  const [valueStart, setValueStart] = useState<DateValueType>(null);
  const [valueEnd, setValueEnd] = useState<DateValueType>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState<TReportBlacklistUser[]>([]);

  const fetchBlacklistUsers = async (params?: TReportBlacklistUsersParams) => {
    const res = await getBlacklistUsers(params);

    setTotalPages(res.total_pages);
    setCurrentPage(res.current_page);
    setData(res.data);
  };

  useEffect(() => {
    fetchBlacklistUsers().then();
  }, []);

  const handleChangePage = async (page: number) => {
    await fetchBlacklistUsers({
      start_date: valueStart?.startDate?.toISOString(),
      end_date: valueEnd?.startDate?.toISOString(),
      page,
    });
  };

  const columns = useMemo(
    (): TColumn<TReportBlacklistUser>[] => [
      {
        title: 'Leeloo Username',
        render: (item) => (
          <p className={'text-blue'}>{item?.leeloo_user.username}</p>
        ),
      },
      {
        title: 'Full Name',
        render: (item) => (
          <p>
            {item?.leeloo_user?.user?.first_name}{' '}
            {item?.leeloo_user?.user?.last_name}
          </p>
        ),
      },
      {
        title: 'Email',
        render: (item) => <p>{item?.leeloo_user?.user?.email}</p>,
      },
      {
        title: 'Blacklisted On',
        render: (item) => <p>{formatDate(item?.date_added)}</p>,
      },
      {
        title: 'Blacklisted By',
        render: (item) => (
          <p>
            {item?.added_by_user?.first_name} {item?.added_by_user?.last_name}
          </p>
        ),
      },
    ],
    []
  );

  const handleGetData = async () => {
    await fetchBlacklistUsers({
      start_date: valueStart?.startDate?.toISOString(),
      end_date: valueEnd?.startDate?.toISOString(),
      page: 1,
    });
  };

  const handleExport = async () => {
    const res = await exportBlacklistUsers({
      start_date: valueStart?.startDate?.toISOString(),
      end_date: valueEnd?.startDate?.toISOString(),
      page: 1,
      size: 20,
    });

    downloadFile(res.data, 'blacklist-users');
  };

  return (
    <div>
      <div
        className={
          'flex justify-between lg:items-center gap-7.5 lg:flex-row flex-col'
        }
      >
        <h1 className={'text-[23px]'}>{`Blacklisted Users`}</h1>
        <Button
          className={'h-9.5 border-1 border-[#C6C6C6] rounded-[3px] w-fit'}
          variant={'outline'}
          onClick={handleExport}
        >
          <p>Export</p>
          <ExportIcon />
        </Button>
      </div>
      <Card className={'mt-4.5'}>
        <div
          className={
            'flex justify-between items-start lg:flex-row flex-col mb-5 gap-6'
          }
        >
          <div className={'w-full flex gap-6 flex-wrap'}>
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created From'}
              size={'sm'}
              value={valueStart}
              variant={'outline'}
              onChange={setValueStart}
            />
            <DateRangePicker
              asSingle={true}
              className={'max-w-[239px]'}
              placeholder={'Created To'}
              size={'sm'}
              value={valueEnd}
              variant={'outline'}
              onChange={setValueEnd}
            />
          </div>
          <div className={'flex gap-5'}>
            <Button size={'sm'} variant={'lime'} onClick={handleGetData}>
              Get
            </Button>
          </div>
        </div>
        <Divider />
        <RadixTable columns={columns} data={data} />
        {totalPages !== 0 ? (
          <div className={'w-full flex justify-end'}>
            <Pagination
              current={currentPage}
              total={totalPages}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
};
